/**
 * Shared blocking-dialog shell for BattleRoom engine prompts.
 *
 * date_of_change: 2026-07-25
 * rule_reference: docs/App_UI_Standard.md Sections 4.6 and 6.2.
 * Keeps prompt presentation and accessibility consistent without owning any
 * combat decision state; callers retain their existing confirm/cancel contracts.
 */
import React, { useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import './BattleRoomDialog.css';

const FOCUSABLE_SELECTOR = [
  'button:not([disabled])',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[href]',
  '[tabindex]:not([tabindex="-1"])'
].join(', ');

const BattleRoomDialog = ({
  visible = false,
  eyebrow = 'BattleRoom',
  title,
  description,
  children,
  footer = null,
  onRequestClose,
  initialFocusSelector = '[data-battle-dialog-autofocus]'
}) => {
  const dialogRef = useRef(null);
  const priorFocusRef = useRef(null);
  const titleId = useId();
  const descriptionId = useId();

  useEffect(() => {
    if (!visible || typeof document === 'undefined') return undefined;

    priorFocusRef.current = document.activeElement;

    const focusInitialControl = () => {
      const dialog = dialogRef.current;
      if (!dialog) return;

      const initialControl = dialog.querySelector(initialFocusSelector)
        || dialog.querySelector(FOCUSABLE_SELECTOR)
        || dialog;
      initialControl.focus();
    };

    const handleKeyDown = (event) => {
      const dialog = dialogRef.current;
      if (!dialog) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        onRequestClose?.();
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableControls = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR));
      if (focusableControls.length === 0) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const firstControl = focusableControls[0];
      const lastControl = focusableControls[focusableControls.length - 1];

      if (event.shiftKey && document.activeElement === firstControl) {
        event.preventDefault();
        lastControl.focus();
      } else if (!event.shiftKey && document.activeElement === lastControl) {
        event.preventDefault();
        firstControl.focus();
      }
    };

    const focusTimer = window.setTimeout(focusInitialControl, 0);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', handleKeyDown);

      const priorFocus = priorFocusRef.current;
      if (priorFocus && typeof priorFocus.focus === 'function' && document.contains(priorFocus)) {
        priorFocus.focus();
      }
    };
  }, [visible, onRequestClose, initialFocusSelector]);

  if (!visible) return null;

  const dialog = (
    <div className="battle-room-dialog-overlay" data-testid="battle-room-dialog-overlay">
      <section
        ref={dialogRef}
        className="battle-room-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description ? descriptionId : undefined}
        tabIndex={-1}
      >
        <header className="battle-room-dialog__header">
          <div className="battle-room-dialog__heading">
            <span className="battle-room-dialog__eyebrow">{eyebrow}</span>
            <h2 id={titleId}>{title}</h2>
            {description && <p id={descriptionId}>{description}</p>}
          </div>
          <button
            type="button"
            className="battle-room-dialog__close"
            onClick={onRequestClose}
            disabled={!onRequestClose}
            aria-label={`Close ${title}`}
          >
            <span aria-hidden="true">x</span>
          </button>
        </header>
        <div className="battle-room-dialog__body">{children}</div>
        {footer && <footer className="battle-room-dialog__footer">{footer}</footer>}
      </section>
    </div>
  );

  return typeof document !== 'undefined' && document.body
    ? createPortal(dialog, document.body)
    : dialog;
};

export default BattleRoomDialog;
